import React, { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CategorySummary, getCategories, getDailyProduct, getDisplayImageUrl, getProduct, getProductPrice, normalizeProductPhotos, Product } from './services/api';
import { RETURN_HOME_EVENT, START_CATEGORY_EVENT, StartCategoryEventDetail } from './events';
import './Game.css';

const ACTIVE_PRODUCT_STORAGE_KEY = 'ebaydle-active-product-v5';
const ACTIVE_CATEGORY_STORAGE_KEY = 'ebaydle-active-category-v1';
const CATEGORY_QUERY_KEY = ['categories-v2'];
const DAILY_PRODUCT_QUERY_KEY = ['daily-product-v3'];
const DAILY_CHALLENGE_CATEGORY = 'Daily Challenge';
const MAX_GUESSES = 4;
const PRICE_DIGIT_COUNT = 7;
const CENTS_DIGIT_START_INDEX = PRICE_DIGIT_COUNT - 2;
const CHALLENGE_ROUNDS = 5;

type GameStatus = 'playing' | 'won' | 'lost';
type DigitResult = 'empty' | 'low' | 'high' | 'match';

interface GuessResult {
    attempt: number;
    digits: string[];
    value: number;
    results: DigitResult[];
}

interface GameState {
    actualPrice: number | null;
    displayPrice: string | null;
    guesses: GuessResult[];
    status: GameStatus;
}

interface ChallengeRoundResult {
    productName: string;
    guessesUsed: number;
    failed: boolean;
}

interface ChallengeState {
    active: boolean;
    results: ChallengeRoundResult[];
    recordedProductId: string | null;
    showResults: boolean;
}

type PendingNavigation =
    | { type: 'home' }
    | { type: 'category'; category: string };

const initialGameState: GameState = {
    actualPrice: null,
    displayPrice: null,
    guesses: [],
    status: 'playing',
};

const initialChallengeState: ChallengeState = {
    active: false,
    results: [],
    recordedProductId: null,
    showResults: false,
};

function gameStateStorageKey(productId: string) {
    return `ebaydle-price-wordle-state-${productId}`;
}

function loadSavedProduct(): Product | null {
    const savedProduct = localStorage.getItem(ACTIVE_PRODUCT_STORAGE_KEY);
    if (!savedProduct) {
        return null;
    }

    try {
        return JSON.parse(savedProduct) as Product;
    } catch {
        localStorage.removeItem(ACTIVE_PRODUCT_STORAGE_KEY);
        return null;
    }
}

function loadSavedCategory(): string | null {
    return localStorage.getItem(ACTIVE_CATEGORY_STORAGE_KEY);
}

function saveActiveRound(product: Product, category: string) {
    localStorage.setItem(ACTIVE_PRODUCT_STORAGE_KEY, JSON.stringify(product));
    localStorage.setItem(ACTIVE_CATEGORY_STORAGE_KEY, category);
}

function clearActiveRound(productId?: string, preserveGameState = false) {
    if (productId && !preserveGameState) {
        localStorage.removeItem(gameStateStorageKey(productId));
    }

    localStorage.removeItem(ACTIVE_PRODUCT_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_CATEGORY_STORAGE_KEY);
}

function loadGameState(productId: string): GameState {
    const savedGameState = localStorage.getItem(gameStateStorageKey(productId));
    if (!savedGameState) {
        return initialGameState;
    }

    try {
        return JSON.parse(savedGameState) as GameState;
    } catch {
        localStorage.removeItem(gameStateStorageKey(productId));
        return initialGameState;
    }
}

const formatGuess = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
    }).format(value);
};

const formatDailyDate = (dateKey?: string) => {
    if (!dateKey) {
        return 'Today';
    }

    const date = new Date(`${dateKey}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) {
        return 'Today';
    }

    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
    }).format(date);
};

const compactDetail = (value: string, maxLength = 130) => {
    const compacted = value.replace(/\s+/g, ' ').trim();
    if (compacted.length <= maxLength) {
        return compacted;
    }

    return `${compacted.slice(0, maxLength).trim()}...`;
};

const createEmptyPriceDigits = () => Array.from({ length: PRICE_DIGIT_COUNT }, () => '');

const centsFromPrice = (price: number) => Math.max(0, Math.round(price * 100));

const priceDigitsFromCents = (cents: number) => {
    return cents
        .toString()
        .padStart(PRICE_DIGIT_COUNT, '0')
        .slice(-PRICE_DIGIT_COUNT)
        .split('');
};

const priceValueFromDigits = (digits: string[]) => {
    const dollars = digits.slice(0, CENTS_DIGIT_START_INDEX).join('') || '0';
    const cents = digits.slice(CENTS_DIGIT_START_INDEX).join('').padEnd(2, '0') || '00';
    return Number(`${dollars}.${cents}`);
};

const comparePriceDigits = (guessDigits: string[], actualDigits: string[]): DigitResult[] => {
    return guessDigits.map((digit, index) => {
        if (!digit) {
            return 'empty';
        }

        const guessDigit = Number(digit);
        const actualDigit = Number(actualDigits[index] || '0');

        if (guessDigit === actualDigit) {
            return 'match';
        }

        return guessDigit < actualDigit ? 'low' : 'high';
    });
};

const sellerPositivePercentage = (product?: Product | null) => {
    const seed = `${product?._id || ''}:${product?.seller_name || ''}:${product?.url || ''}`;
    let hash = 0;

    for (let index = 0; index < seed.length; index += 1) {
        hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
    }

    const percentageTenths = 700 + (hash % 300);
    return (percentageTenths / 10).toFixed(1);
};

const CATEGORY_GROUPS = [
    {
        title: 'Fashion, jewelry, and beauty',
        categories: ['Clothing, Shoes & Accessories', 'Jewelry & Watches', 'Health & Beauty'],
    },
    {
        title: 'Collectibles and media',
        categories: [
            'Antiques',
            'Art',
            'Books',
            'Coins & Paper Money',
            'Collectibles',
            'Dolls & Bears',
            'Entertainment Memorabilia',
            'Movies & TV',
            'Music',
            'Pottery & Glass',
            'Stamps',
        ],
    },
    {
        title: 'Electronics and tech',
        categories: [
            'Cameras & Photo',
            'Cell Phones & Accessories',
            'Computers/Tablets & Networking',
            'Consumer Electronics',
            'Video Games & Consoles',
        ],
    },
    {
        title: 'Home, garden, and daily life',
        categories: [
            'Baby',
            'Business & Industrial',
            'Crafts',
            'Gift Cards & Coupons',
            'Home & Garden',
            'Musical Instruments & Gear',
            'Pet Supplies',
            'Specialty Services',
            'Travel',
        ],
    },
    {
        title: 'Sports, motors, and experiences',
        categories: [
            'eBay Motors',
            'Real Estate',
            'Sporting Goods',
            'Sports Mem, Cards & Fan Shop',
            'Tickets & Experiences',
            'Toys & Hobbies',
        ],
    },
    {
        title: 'More categories',
        categories: ['Everything Else'],
    },
];

const groupCategories = (categories: CategorySummary[]) => {
    const categoriesByName = new Map(categories.map((category) => [category.category, category]));
    const groupedCategoryNames = new Set(CATEGORY_GROUPS.flatMap((group) => group.categories));
    const groups = CATEGORY_GROUPS
        .map((group) => ({
            title: group.title,
            categories: group.categories
                .map((category) => categoriesByName.get(category))
                .filter((category): category is CategorySummary => Boolean(category)),
        }))
        .filter((group) => group.categories.length > 0);
    const ungroupedCategories = categories.filter((category) => !groupedCategoryNames.has(category.category));

    if (ungroupedCategories.length > 0) {
        groups.push({
            title: 'More categories',
            categories: ungroupedCategories,
        });
    }

    return groups;
};

const Game: React.FC = () => {
    const [product, setProduct] = useState<Product | null>(() => loadSavedProduct());
    const [activeCategory, setActiveCategory] = useState<string | null>(() => loadSavedCategory());
    const [currentDigits, setCurrentDigits] = useState<string[]>(() => createEmptyPriceDigits());
    const [formError, setFormError] = useState<string | null>(null);
    const [gameState, setGameState] = useState<GameState>(() => {
        const savedProduct = loadSavedProduct();
        return savedProduct ? loadGameState(savedProduct._id) : initialGameState;
    });
    const [activePhotoIndex, setActivePhotoIndex] = useState(0);
    const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
    const [challengeState, setChallengeState] = useState<ChallengeState>(initialChallengeState);
    const digitInputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const categoriesQuery = useQuery<CategorySummary[]>({
        queryKey: CATEGORY_QUERY_KEY,
        queryFn: async () => {
            const { data } = await getCategories();
            return data;
        },
    });

    const dailyProductQuery = useQuery<Product>({
        queryKey: DAILY_PRODUCT_QUERY_KEY,
        queryFn: async () => {
            const { data } = await getDailyProduct();
            return data;
        },
        retry: false,
    });

    const startGameMutation = useMutation<Product, Error, string | undefined>({
        mutationFn: async (category?: string) => {
            const { data } = await getProduct(category);
            return data;
        },
        onSuccess: (selectedProduct, category) => {
            localStorage.removeItem(gameStateStorageKey(selectedProduct._id));
            saveActiveRound(selectedProduct, category || selectedProduct.category || 'All Categories');
            setProduct(selectedProduct);
            setActiveCategory(category || selectedProduct.category || 'All Categories');
            setGameState(initialGameState);
            setActivePhotoIndex(0);
            setCurrentDigits(createEmptyPriceDigits());
            setFormError(null);
        },
        onError: () => {
            setFormError('Could not start a game for that category. Try another category.');
        },
    });

    const photos: string[] = normalizeProductPhotos(product?.photos || []);
    const mainPhoto = photos[activePhotoIndex] || photos[0];
    const mainPhotoUrl = mainPhoto ? getDisplayImageUrl(mainPhoto) : '';
    const positivePercentage = sellerPositivePercentage(product);
    //const remainingGuesses = MAX_GUESSES - gameState.guesses.length;
    const isCurrentGuessComplete = currentDigits.every((digit) => digit.length === 1);
    const featureEntries: [string, string][] = Object.entries(product?.features || {})
        .filter((entry): entry is [string, string] => {
            return entry[0].toLowerCase() !== 'condition'
                && typeof entry[1] === 'string'
                && entry[1].trim().length > 0;
        })
        .slice(0, 8);
    const condition = product?.features?.Condition || product?.features?.condition || '--';
    const isActiveGame = Boolean(product && gameState.status === 'playing');
    const specificsEntries: [string, string][] = [];

    if (product?.category_path.length) {
        specificsEntries.push(['Category', product.category_path.join(' > ')]);
    }

    if (condition !== '--') {
        specificsEntries.push(['Condition', condition]);
    }

    if (featureEntries.length > 0) {
        featureEntries.forEach(([label, value]) => {
            specificsEntries.push([label, compactDetail(value)]);
        });
    } else if (product) {
        specificsEntries.push(
            ['Seller', product.seller_name],
            ['Photos', String(photos.length)],
        );
    }

    const specificsColumnBreak = Math.ceil(specificsEntries.length / 2);
    const specificsColumns = [
        specificsEntries.slice(0, specificsColumnBreak),
        specificsEntries.slice(specificsColumnBreak),
    ].filter((column) => column.length > 0);
    const challengeCompletedRounds = challengeState.results.length;
    const challengeProgress = (challengeCompletedRounds / CHALLENGE_ROUNDS) * 100;
    const challengeTotalGuesses = challengeState.results.reduce((total, result) => total + result.guessesUsed, 0);
    const challengeFailedRounds = challengeState.results.filter((result) => result.failed).length;
    const challengeScore = Math.max(0, 1000 - challengeTotalGuesses * 20 - challengeFailedRounds * 100);
    const isDailyChallengeRound = activeCategory === DAILY_CHALLENGE_CATEGORY;

    const guessMutation = useMutation({
        mutationFn: async (digits: string[]) => {
            if (!product) {
                throw new Error('No product loaded.');
            }

            const { data } = await getProductPrice(product._id);
            return {
                actualPrice: data.price,
                displayPrice: data.displayPrice,
                digits,
                value: priceValueFromDigits(digits),
            };
        },
        onSuccess: ({ actualPrice, displayPrice, digits, value }) => {
            setGameState((currentState) => {
                if (currentState.status !== 'playing') {
                    return currentState;
                }

                const attempt = currentState.guesses.length + 1;
                const actualDigits = priceDigitsFromCents(centsFromPrice(actualPrice));
                const results = comparePriceDigits(digits, actualDigits);
                const isWinningGuess = results.every((result) => result === 'match');
                const isFinalGuess = attempt >= MAX_GUESSES;
                const status: GameStatus = isWinningGuess ? 'won' : isFinalGuess ? 'lost' : 'playing';

                return {
                    actualPrice: status === 'playing' ? null : actualPrice,
                    displayPrice: status === 'playing' ? null : displayPrice,
                    guesses: [
                        ...currentState.guesses,
                        {
                            attempt,
                            digits,
                            value,
                            results,
                        },
                    ],
                    status,
                };
            });
            setCurrentDigits(createEmptyPriceDigits());
            setFormError(null);
            window.setTimeout(() => digitInputRefs.current[0]?.focus(), 0);
        },
        onError: () => {
            setFormError('Could not check that guess. Try again in a moment.');
        },
    });

    const returnHome = useCallback(() => {
        const shouldPreserveDailyResult = activeCategory === DAILY_CHALLENGE_CATEGORY && gameState.status !== 'playing';
        if (shouldPreserveDailyResult && product?._id) {
            localStorage.setItem(gameStateStorageKey(product._id), JSON.stringify(gameState));
        }
        clearActiveRound(product?._id, shouldPreserveDailyResult);
        setProduct(null);
        setActiveCategory(null);
        setCurrentDigits(createEmptyPriceDigits());
        setFormError(null);
        setGameState(initialGameState);
        setActivePhotoIndex(0);
        setPendingNavigation(null);
        setChallengeState(initialChallengeState);
    }, [activeCategory, gameState, product?._id]);

    const beginCategoryGame = useCallback((category: string) => {
        if (product?._id) {
            const shouldPreserveDailyResult = activeCategory === DAILY_CHALLENGE_CATEGORY && gameState.status !== 'playing';
            if (shouldPreserveDailyResult) {
                localStorage.setItem(gameStateStorageKey(product._id), JSON.stringify(gameState));
            }
            clearActiveRound(product._id, shouldPreserveDailyResult);
        }

        setProduct(null);
        setActiveCategory(category);
        setCurrentDigits(createEmptyPriceDigits());
        setFormError(null);
        setGameState(initialGameState);
        setActivePhotoIndex(0);
        setPendingNavigation(null);
        startGameMutation.mutate(category);
    }, [activeCategory, gameState, product?._id, startGameMutation]);

    const beginRandomGame = useCallback(() => {
        if (product?._id) {
            const shouldPreserveDailyResult = activeCategory === DAILY_CHALLENGE_CATEGORY && gameState.status !== 'playing';
            if (shouldPreserveDailyResult) {
                localStorage.setItem(gameStateStorageKey(product._id), JSON.stringify(gameState));
            }
            clearActiveRound(product._id, shouldPreserveDailyResult);
        }

        setProduct(null);
        setActiveCategory('All Categories');
        setCurrentDigits(createEmptyPriceDigits());
        setFormError(null);
        setGameState(initialGameState);
        setActivePhotoIndex(0);
        setPendingNavigation(null);
        startGameMutation.mutate(undefined);
    }, [activeCategory, gameState, product?._id, startGameMutation]);

    const beginDailyChallenge = useCallback(() => {
        const dailyProduct = dailyProductQuery.data;
        if (!dailyProduct) {
            setFormError('Daily challenge is not ready yet. Try again in a moment.');
            return;
        }

        if (product?._id && product._id !== dailyProduct._id) {
            clearActiveRound(product._id);
        }

        saveActiveRound(dailyProduct, DAILY_CHALLENGE_CATEGORY);
        setProduct(dailyProduct);
        setActiveCategory(DAILY_CHALLENGE_CATEGORY);
        setGameState(loadGameState(dailyProduct._id));
        setActivePhotoIndex(0);
        setCurrentDigits(createEmptyPriceDigits());
        setFormError(null);
        setPendingNavigation(null);
        setChallengeState(initialChallengeState);
    }, [dailyProductQuery.data, product?._id]);

    const startChallenge = useCallback(() => {
        setChallengeState({
            ...initialChallengeState,
            active: true,
        });
        beginRandomGame();
    }, [beginRandomGame]);

    const continueChallenge = useCallback(() => {
        if (challengeState.results.length >= CHALLENGE_ROUNDS) {
            setChallengeState((currentState) => ({
                ...currentState,
                showResults: true,
            }));
            return;
        }

        beginRandomGame();
    }, [beginRandomGame, challengeState.results.length]);

    const requestReturnHome = useCallback(() => {
        if (isActiveGame) {
            setPendingNavigation({ type: 'home' });
            return;
        }

        returnHome();
    }, [isActiveGame, returnHome]);

    const requestCategoryGame = useCallback((category: string) => {
        if (isActiveGame) {
            setPendingNavigation({ type: 'category', category });
            return;
        }

        beginCategoryGame(category);
    }, [beginCategoryGame, isActiveGame]);

    const confirmPendingNavigation = useCallback(() => {
        if (!pendingNavigation) {
            return;
        }

        if (pendingNavigation.type === 'category') {
            beginCategoryGame(pendingNavigation.category);
            return;
        }

        returnHome();
    }, [beginCategoryGame, pendingNavigation, returnHome]);

    useEffect(() => {
        if (!product?._id) {
            setGameState(initialGameState);
            return;
        }

        setGameState(loadGameState(product._id));
        setActivePhotoIndex(0);
        setCurrentDigits(createEmptyPriceDigits());
    }, [product?._id]);

    useEffect(() => {
        const dailyProduct = dailyProductQuery.data;
        const currentProduct = product;
        if (!dailyProduct || !currentProduct || activeCategory !== DAILY_CHALLENGE_CATEGORY || currentProduct._id !== dailyProduct._id) {
            return;
        }

        if (
            currentProduct.name === dailyProduct.name
            && currentProduct.seller_name === dailyProduct.seller_name
            && currentProduct.category === dailyProduct.category
        ) {
            return;
        }

        saveActiveRound(dailyProduct, DAILY_CHALLENGE_CATEGORY);
        setProduct(dailyProduct);
    }, [activeCategory, dailyProductQuery.data, product]);

    useEffect(() => {
        if (!product?._id || gameState.status !== 'playing' || guessMutation.isPending) {
            return;
        }

        const focusTimer = window.setTimeout(() => {
            const firstEmptyIndex = currentDigits.findIndex((digit) => !digit);
            const focusIndex = firstEmptyIndex === -1 ? PRICE_DIGIT_COUNT - 1 : firstEmptyIndex;
            digitInputRefs.current[focusIndex]?.focus();
        }, 80);

        return () => window.clearTimeout(focusTimer);
    }, [currentDigits, gameState.status, guessMutation.isPending, product?._id]);

    useEffect(() => {
        if (!product?._id) {
            return;
        }

        localStorage.setItem(gameStateStorageKey(product._id), JSON.stringify(gameState));
    }, [gameState, product?._id]);

    useEffect(() => {
        if (!challengeState.active || !product?._id || gameState.status === 'playing') {
            return;
        }

        if (challengeState.recordedProductId === product._id) {
            return;
        }

        setChallengeState((currentState) => {
            if (currentState.recordedProductId === product._id) {
                return currentState;
            }

            const nextResults = [
                ...currentState.results,
                {
                    productName: product.name,
                    guessesUsed: gameState.guesses.length,
                    failed: gameState.status === 'lost',
                },
            ].slice(0, CHALLENGE_ROUNDS);

            return {
                ...currentState,
                results: nextResults,
                recordedProductId: product._id,
                showResults: nextResults.length >= CHALLENGE_ROUNDS,
            };
        });
    }, [challengeState.active, challengeState.recordedProductId, gameState.guesses.length, gameState.status, product?._id, product?.name]);

    useEffect(() => {
        const handleReturnHome = () => {
            requestReturnHome();
        };

        const handleStartCategory = (event: Event) => {
            const category = (event as CustomEvent<StartCategoryEventDetail>).detail?.category;
            if (category) {
                requestCategoryGame(category);
            }
        };

        window.addEventListener(RETURN_HOME_EVENT, handleReturnHome);
        window.addEventListener(START_CATEGORY_EVENT, handleStartCategory);
        return () => {
            window.removeEventListener(RETURN_HOME_EVENT, handleReturnHome);
            window.removeEventListener(START_CATEGORY_EVENT, handleStartCategory);
        };
    }, [requestCategoryGame, requestReturnHome]);

    const statusMessage = useMemo(() => {
        if (gameState.status === 'won') {
            return `You won! The price was ${gameState.displayPrice || formatGuess(gameState.actualPrice || 0)}.`;
        }

        if (gameState.status === 'lost') {
            return `You lose! The price was ${gameState.displayPrice || formatGuess(gameState.actualPrice || 0)}.`;
        }
    }, [gameState]);

    const statusClass = gameState.status;

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!isCurrentGuessComplete) {
            setFormError('Fill every price digit before submitting.');
            return;
        }

        if (gameState.status !== 'playing') {
            return;
        }

        guessMutation.mutate([...currentDigits]);
    }

    function handleDigitChange(index: number, rawValue: string) {
        const nextDigit = rawValue.replace(/\D/g, '').slice(-1);

        setCurrentDigits((digits) => {
            const nextDigits = [...digits];
            nextDigits[index] = nextDigit;
            return nextDigits;
        });

        setFormError(null);

        if (nextDigit && index < PRICE_DIGIT_COUNT - 1) {
            digitInputRefs.current[index + 1]?.focus();
        }
    }

    function handleDigitKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'Backspace' && !currentDigits[index] && index > 0) {
            event.preventDefault();
            setCurrentDigits((digits) => {
                const nextDigits = [...digits];
                nextDigits[index - 1] = '';
                return nextDigits;
            });
            digitInputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            digitInputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === 'ArrowRight' && index < PRICE_DIGIT_COUNT - 1) {
            event.preventDefault();
            digitInputRefs.current[index + 1]?.focus();
        }
    }

    function handleDigitPaste(index: number, event: React.ClipboardEvent<HTMLInputElement>) {
        const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, PRICE_DIGIT_COUNT - index);
        if (!pastedDigits) {
            return;
        }

        event.preventDefault();
        setCurrentDigits((digits) => {
            const nextDigits = [...digits];
            pastedDigits.split('').forEach((digit, offset) => {
                nextDigits[index + offset] = digit;
            });
            return nextDigits;
        });

        const nextIndex = Math.min(index + pastedDigits.length, PRICE_DIGIT_COUNT - 1);
        window.setTimeout(() => digitInputRefs.current[nextIndex]?.focus(), 0);
    }

    function renderPriceRow(rowIndex: number) {
        const completedGuess = gameState.guesses[rowIndex];
        const isActiveRow = rowIndex === gameState.guesses.length && gameState.status === 'playing';
        const digits = completedGuess?.digits || (isActiveRow ? currentDigits : createEmptyPriceDigits());
        const rowClassName = [
            'price-guess-row',
            completedGuess ? 'submitted' : '',
            isActiveRow ? 'active' : '',
        ].filter(Boolean).join(' ');

        return (
            <div className={rowClassName} key={`price-row-${rowIndex}`} aria-label={`Guess ${rowIndex + 1}`}>
                {digits.map((digit, index) => {
                    const resultClass = completedGuess
                        ? completedGuess.results[index]
                        : digit
                            ? 'filled'
                            : 'empty';
                    const tileClassName = ['price-tile', resultClass].filter(Boolean).join(' ');

                    return (
                        <React.Fragment key={`price-row-${rowIndex}-digit-${index}`}>
                            <div
                                className={tileClassName}
                                style={completedGuess ? { animationDelay: `${index * 90}ms` } : undefined}
                            >
                                {isActiveRow ? (
                                    <input
                                        ref={(element) => {
                                            digitInputRefs.current[index] = element;
                                        }}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={1}
                                        value={digit}
                                        disabled={guessMutation.isPending}
                                        aria-label={`Price digit ${index + 1}`}
                                        onChange={(event) => handleDigitChange(index, event.target.value)}
                                        onKeyDown={(event) => handleDigitKeyDown(index, event)}
                                        onPaste={(event) => handleDigitPaste(index, event)}
                                        onFocus={(event) => event.currentTarget.select()}
                                    />
                                ) : (
                                    <span>{digit}</span>
                                )}
                            </div>
                            {index === CENTS_DIGIT_START_INDEX - 1 && <span className="price-decimal">.</span>}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    function movePhoto(direction: 1 | -1) {
        if (photos.length <= 1) {
            return;
        }

        setActivePhotoIndex((currentIndex) => {
            return (currentIndex + direction + photos.length) % photos.length;
        });
    }

    function handleCategorySelect(category: string) {
        setFormError(null);
        requestCategoryGame(category);
    }

    function handleRetryCategory() {
        const category = activeCategory || product?.category;
        if (category) {
            beginCategoryGame(category);
        }
    }

    const categories: CategorySummary[] = categoriesQuery.data || [];
    const visibleCategories = categories.filter((category) => category.category !== 'Everything Else');
    const categoryGroups = groupCategories(visibleCategories);
    const dailyProduct = dailyProductQuery.data || null;
    const dailyPhoto = normalizeProductPhotos(dailyProduct?.photos || [])[0];
    const dailyPhotoUrl = dailyPhoto ? getDisplayImageUrl(dailyPhoto) : '';
    const dailyDateLabel = formatDailyDate(dailyProduct?.daily_challenge_date);

    if (!product) {
        return (
            <main className="game-page">
                <section className="daily-challenge-banner">
                    <div className="daily-challenge-copy">
                        <p>{dailyDateLabel}</p>
                        <h2>
                            Daily Challenge
                            <br />
                            One Item.
                            <br />
                            Four Guesses.
                        </h2>
                        <div className="challenge-banner-actions">
                            <button
                                type="button"
                                onClick={beginDailyChallenge}
                                disabled={!dailyProduct || dailyProductQuery.isLoading}
                            >
                                Play Daily Challenge
                            </button>
                        </div>
                        {dailyProductQuery.isError && (
                            <span className="daily-challenge-error">Daily challenge is not available yet.</span>
                        )}
                    </div>
                    <div className="daily-challenge-art" aria-hidden="true">
                        {dailyPhotoUrl ? (
                            <img src={dailyPhotoUrl} alt="" />
                        ) : (
                            <div className="daily-challenge-placeholder" />
                        )}
                    </div>
                </section>

                <section className="challenge-banner">
                    <div className="challenge-banner-copy">
                        <h2>Start a Guessing Spree</h2>
                        <span>Guess five random listings and try to get the best possible score, or play one item at a time.</span>
                        <div className="challenge-banner-actions">
                            <button type="button" onClick={startChallenge} disabled={startGameMutation.isPending}>
                                Start Spree
                            </button>
                            <button type="button" onClick={beginRandomGame} disabled={startGameMutation.isPending}>
                                Random Item
                            </button>
                        </div>
                    </div>
                    <div className="challenge-banner-tiles" aria-hidden="true">
                        {categories.slice(0, 3).map((category, index) => (
                            <div className={`challenge-banner-tile tile-${index + 1}`} key={category.category}>
                                <img src={category.imageUrl} alt="" />
                            </div>
                        ))}
                    </div>
                </section>

                <section className="category-picker" aria-label="Collected item categories">
                    {categoriesQuery.isLoading && <p className="loading-state">Loading categories...</p>}
                    {categoriesQuery.isError && <p className="form-error">Could not load categories from MongoDB.</p>}
                    {!categoriesQuery.isLoading && categories.length === 0 && (
                        <p className="loading-state">No categories have been collected yet.</p>
                    )}

                    {categoryGroups.map((group) => (
                        <section className="category-directory-group" key={group.title}>
                            <h2>{group.title}</h2>
                            <div className="category-directory">
                                {group.categories.map((category) => (
                                    <button
                                        className="category-directory-link"
                                        key={category.category}
                                        type="button"
                                        onClick={() => handleCategorySelect(category.category)}
                                        disabled={startGameMutation.isPending}
                                    >
                                        <span className="category-directory-thumb">
                                            {category.imageUrl && <img src={category.imageUrl} alt="" />}
                                        </span>
                                        <span>{category.category}</span>
                                    </button>
                                ))}
                            </div>
                        </section>
                    ))}

                    {startGameMutation.isPending && <p className="loading-state">Choosing a random item...</p>}
                    {formError && <p className="form-error">{formError}</p>}
                </section>
            </main>
        );
    }

    return (
        <main className="game-page">
            <div className="listing-layout">
                <section className="gallery-section" aria-label="Product images">
                        <div className="thumbnail-rail">
                            {photos.map((photo, index) => (
                                <button
                                    className={index === activePhotoIndex ? 'thumbnail active' : 'thumbnail'}
                                    key={`${photo}-${index}`}
                                    type="button"
                                    onClick={() => setActivePhotoIndex(index)}
                                    aria-label={`View image ${index + 1}`}
                                >
                                    <img src={getDisplayImageUrl(photo)} alt="" />
                                </button>
                            ))}
                        </div>

                        <div className="image-stage">
                            {mainPhotoUrl ? (
                                <img src={mainPhotoUrl} alt={product.name} />
                            ) : (
                                <div className="image-placeholder">No image</div>
                            )}
                            <button className="carousel-button previous" type="button" onClick={() => movePhoto(-1)} aria-label="Previous image">
                                {'<'}
                            </button>
                            <button className="carousel-button next" type="button" onClick={() => movePhoto(1)} aria-label="Next image">
                                {'>'}
                            </button>
                        </div>
                </section>

                <section className="listing-game-panel" aria-label="Listing information and guessing game">
                    <section className="listing-title-section">
                        <p className="category-eyebrow">{activeCategory || product.category}</p>
                        <h2>{product.name}</h2>
                        <div className="seller-card">
                            <div className="seller-avatar">{product.seller_name.charAt(0).toUpperCase()}</div>
                            <div>
                                <strong>{product.seller_name}</strong>
                                <p>
                                    <span>{positivePercentage}% positive</span>
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className={`guess-card ${statusClass}`} aria-label="Price guessing game">
                        {statusMessage != null && (
                            <div className="guess-status">
                                <strong>{statusMessage}</strong>
                            </div>
                        )}

                        <form className="price-wordle-form" onSubmit={handleSubmit}>
                            <span className="price-entry-label">Price guess</span>
                            <div className="price-wordle-board" aria-live="polite">
                                {Array.from({ length: MAX_GUESSES }, (_, rowIndex) => renderPriceRow(rowIndex))}
                            </div>
                            <div className="price-wordle-actions">
                                <div className="price-legend">
                                    <button
                                        className="price-legend-button"
                                        type="button"
                                        aria-describedby="price-legend-tooltip"
                                        aria-label="Price color legend"
                                    >
                                        ?
                                    </button>
                                    <div className="price-legend-popover" id="price-legend-tooltip" role="tooltip">
                                        <span><i className="legend-swatch high" />Down arrow: target is lower</span>
                                        <span><i className="legend-swatch low" />Up arrow: target is higher</span>
                                        <span><i className="legend-swatch match" />Correct number</span>
                                    </div>
                                </div>
                                <button
                                    className="primary-button"
                                    type="submit"
                                    disabled={gameState.status !== 'playing' || guessMutation.isPending || !isCurrentGuessComplete}
                                >
                                    {guessMutation.isPending ? 'Checking' : 'Check'}
                                </button>
                            </div>
                            {formError && <p className="form-error">{formError}</p>}
                        </form>

                        {gameState.status !== 'playing' && (
                            <div className="round-actions">
                                <a className="original-listing-button" href={product.url} target="_blank" rel="noreferrer">
                                    View original listing
                                </a>
                                {challengeState.active ? (
                                    <>
                                        <button className="secondary-button" type="button" onClick={returnHome}>Exit Challenge</button>
                                        <button
                                            className="primary-button"
                                            type="button"
                                            onClick={continueChallenge}
                                            disabled={startGameMutation.isPending}
                                        >
                                            {challengeState.results.length >= CHALLENGE_ROUNDS ? 'Show Results' : 'Next Item'}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button className="secondary-button" type="button" onClick={returnHome}>Return to Home</button>
                                        {!isDailyChallengeRound && (
                                            <button
                                                className="primary-button"
                                                type="button"
                                                onClick={handleRetryCategory}
                                                disabled={startGameMutation.isPending}
                                            >
                                                Retry Category
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </section>
                </section>

                <section className="details-panel">
                        <h3>Item specifics</h3>
                        <div className="specifics-columns">
                            {specificsColumns.map((column, columnIndex) => (
                                <dl className="specifics-column" key={`specifics-column-${columnIndex}`}>
                                    {column.map(([label, value]) => (
                                        <React.Fragment key={`${columnIndex}-${label}`}>
                                            <dt>{label}</dt>
                                            <dd>{value}</dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            ))}
                        </div>
                        {specificsEntries.length === 0 && (
                            <p className="details-empty-state">No item specifics listed.</p>
                        )}
                </section>
            </div>

            {pendingNavigation && product && (
                <div className="modal-backdrop">
                    <section className="leave-modal" role="dialog" aria-modal="true" aria-labelledby="leave-game-title">
                        <button
                            className="modal-close"
                            type="button"
                            aria-label="Close"
                            onClick={() => setPendingNavigation(null)}
                        >
                            x
                        </button>
                        <div className="leave-modal-product">
                            {mainPhotoUrl ? (
                                <img src={mainPhotoUrl} alt="" />
                            ) : (
                                <div className="leave-modal-placeholder">No image</div>
                            )}
                            <div>
                                <h2 id="leave-game-title">Leave current game?</h2>
                                <p>{product.name}</p>
                                <span>Your current guesses will be cleared.</span>
                            </div>
                        </div>
                        <button className="leave-game-button" type="button" onClick={confirmPendingNavigation}>
                            leave Game
                        </button>
                    </section>
                </div>
            )}

            {challengeState.active && product && (
                <section className="challenge-progress-bar" aria-label="Challenge progress">
                    <div>
                        <strong>Shopping Spree</strong>
                        <span>Completed {challengeCompletedRounds} of {CHALLENGE_ROUNDS}</span>
                    </div>
                    <div className="challenge-progress-track">
                        <span style={{ width: `${challengeProgress}%` }} />
                    </div>
                    <div>
                        <span>{challengeTotalGuesses} guesses</span>
                        <span>{challengeFailedRounds} misses</span>
                    </div>
                </section>
            )}

            {challengeState.active && challengeState.showResults && (
                <div className="modal-backdrop">
                    <section className="challenge-results-modal" role="dialog" aria-modal="true" aria-labelledby="challenge-results-title">
                        <button
                            className="modal-close"
                            type="button"
                            aria-label="Close"
                            onClick={() => setChallengeState((currentState) => ({ ...currentState, showResults: false }))}
                        >
                            x
                        </button>
                        <p>Shopping Spree results</p>
                        <h2 id="challenge-results-title">Final score: {challengeScore}</h2>
                        <div className="score-breakdown">
                            <strong>Score calculation</strong>
                            <span>Start with 1000 points</span>
                            <span>- {challengeTotalGuesses} guesses x 20 = -{challengeTotalGuesses * 20}</span>
                            <span>- {challengeFailedRounds} failed rounds x 100 = -{challengeFailedRounds * 100}</span>
                            <em>1000 - {challengeTotalGuesses * 20} - {challengeFailedRounds * 100} = {challengeScore}</em>
                        </div>
                        <div className="challenge-result-stats">
                            <div>
                                <strong>{challengeTotalGuesses}</strong>
                                <span>Total guesses</span>
                            </div>
                            <div>
                                <strong>{challengeFailedRounds}</strong>
                                <span>Failed rounds</span>
                            </div>
                            <div>
                                <strong>{CHALLENGE_ROUNDS - challengeFailedRounds}</strong>
                                <span>Wins</span>
                            </div>
                        </div>

                        <div className="round-actions">
                            <button className="secondary-button" type="button" onClick={returnHome}>Return Home</button>
                            <button className="primary-button" type="button" onClick={startChallenge}>Play Again</button>
                        </div>
                    </section>
                </div>
            )}
        </main>
    );
};

export default Game;
